import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { ColumnMenuComponent } from './subcomponents/column-menu/column-menu.component';
import { Store } from '@ngrx/store';
import { combineLatestWith, Observable, Subject, switchMap } from 'rxjs';
import { LocationListService } from '../location-list.service';
import { OngoingActionsEnum } from '../../enums/ongoing-actions';
import { sendToGoogleAnalytics } from '../../../../core/utilities';
import { filter, map, take, takeUntil } from 'rxjs/operators';
import { HotkeysService } from '../../../global/hotkeys/hotkeys.service';
import * as locationListSelectors from '../state/location-list-selectors';
import { selectSelectedRowsIds } from '../state/location-list-selectors';
import { LocationLevel } from '../../enums/location-level';
import { ExportFormat } from '../../enums/export-format';
import { LocationsRefreshActions } from '../state/location-list-actions';
import { TranslateService } from '@ngx-translate/core';
import { LocationListLocalStoreService } from '../location-list-local-store.service';
import { DATA_STALE_AFTER_HR } from '../../constants';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { MatBadge } from '@angular/material/badge';
import { RestartDatasyncComponent } from './subcomponents/restart-datasync/restart-datasync.component';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { GtagEventDirective } from '../../../../core/gx-gtag/gtag-event.directive';
import { DecimalPipe, NgClass } from '@angular/common';
import { MatProgressBar } from '@angular/material/progress-bar';
import { BreadcrumbsComponent } from '../../../global/breadcrumbs/breadcrumbs.component';
import { LetDirective } from '@ngrx/component';
import { CCSyncStatus } from '../../../../models/locations-models/cc-sync-details';
import { RestartCcSyncComponent } from './subcomponents/restart-cc-sync/restart-cc-sync.component';
import { BulkValidateComponent } from './subcomponents/bulk-validate/bulk-validate.component';

@Component({
    selector: 'app-location-list-action-bar',
    templateUrl: './location-list-action-bar.component.html',
    styleUrls: ['./location-list-action-bar.component.scss'],
    // Allow the stylesheet to apply to the subcomponents too.
    // The selectors are wrapped under #address-list-action-bar
    // so no styles will affect any unrelated components.
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
  imports: [
    LetDirective,
    BreadcrumbsComponent,
    MatProgressBar,
    NgClass,
    GtagEventDirective,
    MatIcon,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    MatDivider,
    RestartDatasyncComponent,
    MatBadge,
    MatTooltip,
    MatProgressSpinner,
    ColumnMenuComponent,
    DecimalPipe,
    RestartCcSyncComponent,
    BulkValidateComponent,
  ],
})
export class LocationListActionBarComponent implements OnInit, OnDestroy {

  // Import locations
  @ViewChild('importFileInput') importFileInput;

  // Columns visibility menu trigger
  @ViewChild('columnMenuTrigger') columnMenuTrigger: MatMenuTrigger;

  // Columns visibility menu
  @ViewChild(ColumnMenuComponent, { static: true }) columnMenu;

  // HTML use
  DATA_STALE_AFTER_HR = DATA_STALE_AFTER_HR;

  locationListLoading$ = this.store.select(locationListSelectors.selectLoading);
  ongoingActions$ = this.store.select(locationListSelectors.selectOngoingActions);
  canTransfer$ = this.store.select(locationListSelectors.selectCanTransfer());
  canBulkAcceptDiffs$ = this.store.select(locationListSelectors.selectCanBulkAcceptDiffs);
  isSortingApplied$ = this.locationListLocalStore.isSortingApplied$;
  areFiltersApplied$ = this.locationListLocalStore.filtersCount$
      .pipe(map(filtersCount => !!filtersCount));
  filtersCount$ = this.locationListLocalStore.filtersCount$;
  areRowsSelected$ = this.store.select(locationListSelectors.selectAreRowsSelected);
  selectedRowsIds$ = this.store.select(selectSelectedRowsIds);
  total$ = this.locationListLocalStore.total$;

  combined$: Observable<{
    ongoingActions: string[],
    canTransfer: boolean,
    canCopyToClipboard: boolean,
    canUpdateInGbp: boolean,
    canArchive: boolean,
    canBlacklist: boolean,
    canRefreshGbp: boolean,
    canImport: boolean,
    canExport: boolean,
    canBulkAcceptDiffs: boolean,
    isSortingApplied: boolean,
    areFiltersApplied: boolean,
    filtersCount: number,
    loading: boolean,
    areRowsSelected: boolean,
    selectedRowsCount: number,
    total: number,
    potentiallyStaleList: boolean,
  }> = this.ongoingActions$.pipe(
      combineLatestWith(this.canTransfer$,
          this.store.select(locationListSelectors.selectCanRunAction()),
          this.store.select(locationListSelectors.selectCanRunAction(OngoingActionsEnum.UPDATE_IN_GBP)),
          this.store.select(locationListSelectors.selectCanRunAction(OngoingActionsEnum.ARCHIVING)),
          this.store.select(locationListSelectors.selectCanRunAction(OngoingActionsEnum.BLACKLISTING)),
          this.store.select(locationListSelectors.selectCanRunAction(OngoingActionsEnum.GET_UPDATES_FROM_GBP)),
          this.store.select(locationListSelectors.selectCanRunAction(OngoingActionsEnum.IMPORTING)),
          this.store.select(locationListSelectors.selectCanRunAction(OngoingActionsEnum.EXPORTING)),
          this.canBulkAcceptDiffs$,
          this.isSortingApplied$,
          this.areFiltersApplied$,
          this.filtersCount$,
          this.locationListLoading$,
          this.areRowsSelected$,
          this.selectedRowsIds$,
          this.total$,
          this.locationListLocalStore.potentiallyStaleList$,
      ), map((
          [ongoingActions, canTransfer, canCopyToClipboard, canUpdateInGbp, canArchive, canBlacklist,
            canRefreshGbp, canImport, canExport, canBulkAcceptDiffs, isSortingApplied, areFiltersApplied,
            filtersCount, loading, areRowsSelected, selectedRowsIds, total, potentiallyStaleList]) => {
        return {
          ongoingActions,
          canTransfer,
          canCopyToClipboard,
          canUpdateInGbp,
          canArchive,
          canBlacklist,
          canRefreshGbp,
          canImport,
          canExport,
          canBulkAcceptDiffs,
          isSortingApplied,
          areFiltersApplied,
          filtersCount,
          loading,
          areRowsSelected,
          selectedRowsCount: selectedRowsIds.length,
          total,
          potentiallyStaleList,
        };
      }));

  componentDestroyed = new Subject<void>();

  // HTML use
  locationLevel = LocationLevel;
  format = ExportFormat;
  ccSyncStatus = CCSyncStatus;

  constructor(
      private store: Store,
      private locationListLocalStore: LocationListLocalStoreService,
      public locationListService: LocationListService,
      public translate: TranslateService,
      private hotkeysService: HotkeysService,
  ) {
  }

  ngOnDestroy() {
    this.componentDestroyed.next();
  }

  ngOnInit(): void {
    this.registerHotkeys();
  }

  /**
   * Register hotkeys
   */
  registerHotkeys() {
    this.hotkeysService.addHotkeyAndGetEventFeed('c', 'Copy selected locations to clipboard', this.componentDestroyed)
        .pipe(
            takeUntil(this.componentDestroyed),
            switchMap(() => this.store.select(locationListSelectors.selectCanRunAction()).pipe(take(1))),
            filter(Boolean),
        )
        .subscribe(() => {
          this.locationListService.copyStringToClipboard();
        });

    this.hotkeysService.addHotkeyAndGetEventFeed('v', 'Columns panel visibility', this.componentDestroyed)
        .pipe(takeUntil(this.componentDestroyed))
        .subscribe(() => {
          this.columnMenuTrigger?.toggleMenu();
        });

    this.hotkeysService.addHotkeyAndGetEventFeed('u', 'Update in GBP', this.componentDestroyed)
        .pipe(
            takeUntil(this.componentDestroyed),
            switchMap(() => this.store.select(locationListSelectors.selectCanRunAction(OngoingActionsEnum.UPDATE_IN_GBP)).pipe(take(1))),
            filter(Boolean),
        )
        .subscribe(() => {
          this.locationListService.updateInGbpAndSendToGoogleAnalytics();
        });

    this.hotkeysService.addHotkeyAndGetEventFeed('y', 'Clear sorting', this.componentDestroyed)
        .pipe(
            takeUntil(this.componentDestroyed),
            switchMap(() => this.isSortingApplied$.pipe(take(1))),
            filter(Boolean),
        )
        .subscribe(() => {
          this.locationListService.clearSorting();
        });

    this.hotkeysService.addHotkeyAndGetEventFeed('x', 'Clear filters', this.componentDestroyed)
        .pipe(takeUntil(this.componentDestroyed),
            switchMap(() => this.areFiltersApplied$.pipe(take(1))),
            filter(Boolean),
        )
        .subscribe(() => {
          this.locationListService.clearFilters();
        });
  }

  /**
   * Action dispatcher
   */
  updateListData() {
    // this.store.dispatch(LocationsRefreshActions.getLocationList({}));
    this.store.dispatch(LocationsRefreshActions.getEntireLocationList({}));
  }

  /**
   * Opens a tab where a csv file can be selected.
   */
  chooseImportFile() {
    this.importFileInput.nativeElement.click();
  }

  /**
   * Uploads the csv file if one has just been selected.
   */
  onImportFileSelected() {
    this.locationListService.onImportFileSelected(this.importFileInput.nativeElement.files[0])
        .add(() => {
          this.importFileInput.nativeElement.value = '';  //do this on success as well as on error
          sendToGoogleAnalytics('Import location data',
              'Import location data',
              'Import location data');
        });
  }

}
